import { useState, useMemo } from 'react';
import { useSchedule, useUpsertShift, useDeleteShift, useUpdateRotationConfig } from '../hooks/useSchedule';
import { useUsers } from '../hooks/useUsers';
import type { ScheduleWeek, RotationConfig } from '../types';

function getMonday(dateStr: string): string {
  const d = new Date(dateStr + 'T12:00:00Z');
  const day = d.getUTCDay();
  const diff = day === 0 ? -6 : 1 - day;
  d.setUTCDate(d.getUTCDate() + diff);
  return d.toISOString().split('T')[0];
}

function formatWeekRange(weekStart: string, weekEnd: string): string {
  const start = new Date(weekStart + 'T12:00:00Z');
  const end = new Date(weekEnd + 'T12:00:00Z');
  const opts: Intl.DateTimeFormatOptions = { month: 'short', day: 'numeric' };
  const startStr = start.toLocaleDateString('en-US', opts);
  const endStr = end.toLocaleDateString('en-US', { ...opts, year: 'numeric' });
  return `${startStr} – ${endStr}`;
}

function isCurrentWeek(weekStart: string): boolean {
  const today = new Date().toISOString().split('T')[0];
  return getMonday(today) === weekStart;
}

function isPastWeek(weekStart: string): boolean {
  const today = new Date().toISOString().split('T')[0];
  return weekStart < getMonday(today);
}

const WEEKS_PER_PAGE = 16;
const PAST_WEEKS = 4;

export function Schedule() {
  const [pageOffset, setPageOffset] = useState(0);
  const [editingRotation, setEditingRotation] = useState(false);

  // Compute start Monday for the current page
  const startMonday = useMemo(() => {
    const today = new Date().toISOString().split('T')[0];
    const thisMonday = getMonday(today);
    const d = new Date(thisMonday + 'T12:00:00Z');
    d.setUTCDate(d.getUTCDate() - PAST_WEEKS * 7 + pageOffset * WEEKS_PER_PAGE * 7);
    return d.toISOString().split('T')[0];
  }, [pageOffset]);

  const { data, isLoading } = useSchedule(startMonday, WEEKS_PER_PAGE);
  const { data: users } = useUsers();
  const upsertShift = useUpsertShift();
  const deleteShift = useDeleteShift();
  const updateRotation = useUpdateRotationConfig();

  // Rotation config editing state
  const [rotationEnabled, setRotationEnabled] = useState(false);
  const [rotationMembers, setRotationMembers] = useState<Array<{ email: string; name: string }>>([]);
  const [rotationStartDate, setRotationStartDate] = useState('');
  const [rotationPeriod, setRotationPeriod] = useState(7);
  const [rotationError, setRotationError] = useState<string | null>(null);

  const startEditingRotation = () => {
    const config = data?.rotationConfig;
    setRotationEnabled(config?.enabled ?? false);
    setRotationMembers(config?.members ?? []);
    setRotationStartDate(config?.startDate ?? getMonday(new Date().toISOString().split('T')[0]));
    setRotationPeriod(config?.periodDays ?? 7);
    setRotationError(null);
    setEditingRotation(true);
  };

  const handleSaveRotation = async () => {
    setRotationError(null);

    if (getMonday(rotationStartDate) !== rotationStartDate) {
      setRotationError('Start date must be a Monday');
      return;
    }

    if (rotationEnabled && rotationMembers.length === 0) {
      setRotationError('Add at least one member to enable rotation');
      return;
    }

    try {
      await updateRotation.mutateAsync({
        enabled: rotationEnabled,
        members: rotationMembers,
        startDate: rotationStartDate,
        periodDays: rotationPeriod,
      });
      setEditingRotation(false);
    } catch (err) {
      setRotationError(err instanceof Error ? err.message : 'Failed to save rotation config');
    }
  };

  const handleAssign = async (week: ScheduleWeek, email: string, name: string) => {
    await upsertShift.mutateAsync({
      weekStart: week.weekStart,
      assigneeEmail: email,
      assigneeName: name,
    });
  };

  const handleClearOverride = async (weekStart: string) => {
    await deleteShift.mutateAsync(weekStart);
  };

  const addRotationMember = (email: string, name: string) => {
    if (rotationMembers.some((m) => m.email === email)) return;
    setRotationMembers([...rotationMembers, { email, name }]);
  };

  const removeRotationMember = (email: string) => {
    setRotationMembers(rotationMembers.filter((m) => m.email !== email));
  };

  const moveRotationMember = (index: number, direction: -1 | 1) => {
    const newIndex = index + direction;
    if (newIndex < 0 || newIndex >= rotationMembers.length) return;
    const updated = [...rotationMembers];
    [updated[index], updated[newIndex]] = [updated[newIndex], updated[index]];
    setRotationMembers(updated);
  };

  return (
    <div className="space-y-6">
      <h1 className="text-2xl font-bold text-gray-900">On-Call Schedule</h1>

      {/* Week Grid */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200 bg-gray-50">
          <button
            onClick={() => setPageOffset((p) => p - 1)}
            className="px-3 py-1.5 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50"
          >
            Previous
          </button>
          <span className="text-sm font-medium text-gray-600">
            {data?.weeks?.[0] && data?.weeks?.[data.weeks.length - 1]
              ? `${formatWeekRange(data.weeks[0].weekStart, data.weeks[data.weeks.length - 1].weekEnd)}`
              : 'Loading...'}
          </span>
          <button
            onClick={() => setPageOffset((p) => p + 1)}
            className="px-3 py-1.5 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50"
          >
            Next
          </button>
        </div>

        <table className="min-w-full divide-y divide-gray-200">
          <thead className="bg-gray-50">
            <tr>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Week
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Assigned To
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Source
              </th>
              <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                Actions
              </th>
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200">
            {isLoading ? (
              Array.from({ length: 6 }).map((_, i) => (
                <tr key={i}>
                  {Array.from({ length: 4 }).map((_, j) => (
                    <td key={j} className="px-6 py-4">
                      <span className="animate-pulse bg-gray-200 h-4 w-24 rounded inline-block" />
                    </td>
                  ))}
                </tr>
              ))
            ) : data?.weeks?.length ? (
              data.weeks.map((week) => {
                const current = isCurrentWeek(week.weekStart);
                const past = isPastWeek(week.weekStart);
                return (
                  <tr
                    key={week.weekStart}
                    className={
                      current
                        ? 'bg-blue-50/50 border-l-4 border-l-blue-500'
                        : past
                          ? 'bg-gray-50/50'
                          : ''
                    }
                  >
                    <td className={`px-6 py-4 whitespace-nowrap text-sm font-medium ${past ? 'text-gray-400' : 'text-gray-900'}`}>
                      {formatWeekRange(week.weekStart, week.weekEnd)}
                      {current && (
                        <span className="ml-2 text-xs text-blue-600 font-normal">(current)</span>
                      )}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm">
                      {past ? (
                        <span className={`text-sm ${week.assigneeName ? 'text-gray-500' : 'text-gray-400 italic'}`}>
                          {week.assigneeName || 'Unassigned'}
                        </span>
                      ) : (
                        <select
                          value={week.assigneeEmail ?? ''}
                          onChange={(e) => {
                            const email = e.target.value;
                            if (!email) {
                              if (week.source === 'manual') handleClearOverride(week.weekStart);
                              return;
                            }
                            const u = users?.find((u) => u.email === email);
                            if (u) handleAssign(week, email, u.name || email);
                          }}
                          disabled={upsertShift.isPending || deleteShift.isPending}
                          className="block w-full max-w-xs px-3 py-1.5 text-sm border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 disabled:opacity-50"
                        >
                          <option value="">Unassigned</option>
                          {users?.map((u) => (
                            <option key={u.email} value={u.email}>
                              {u.name || u.email}
                            </option>
                          ))}
                        </select>
                      )}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span
                        className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                          week.source === 'manual'
                            ? 'bg-blue-100 text-blue-800'
                            : week.source === 'rotation'
                              ? 'bg-green-100 text-green-800'
                              : 'bg-gray-100 text-gray-500'
                        }`}
                      >
                        {week.source === 'manual' ? 'Manual' : week.source === 'rotation' ? 'Auto' : 'None'}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-right text-sm">
                      {week.source === 'manual' && !past && (
                        <button
                          onClick={() => handleClearOverride(week.weekStart)}
                          disabled={deleteShift.isPending}
                          className="text-red-600 hover:text-red-900 disabled:opacity-40"
                        >
                          Clear override
                        </button>
                      )}
                    </td>
                  </tr>
                );
              })
            ) : (
              <tr>
                <td colSpan={4} className="px-6 py-8 text-center text-sm text-gray-500">
                  No schedule data available.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {/* Rotation Settings */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-lg font-semibold text-gray-900">Rotation Settings</h2>
          {!editingRotation && (
            <button
              onClick={startEditingRotation}
              className="px-4 py-2 bg-blue-600 text-white text-sm font-medium rounded-md hover:bg-blue-700"
            >
              {data?.rotationConfig ? 'Edit' : 'Configure'}
            </button>
          )}
        </div>

        {!editingRotation ? (
          <RotationDisplay config={data?.rotationConfig ?? null} />
        ) : (
          <div className="space-y-4">
            {/* Enable toggle */}
            <label className="flex items-center gap-3">
              <input
                type="checkbox"
                checked={rotationEnabled}
                onChange={(e) => setRotationEnabled(e.target.checked)}
                className="h-4 w-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
              />
              <span className="text-sm font-medium text-gray-700">Enable automatic rotation</span>
            </label>

            {/* Start date */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Start date (must be a Monday)
              </label>
              <input
                type="date"
                value={rotationStartDate}
                onChange={(e) => setRotationStartDate(e.target.value)}
                className="px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm"
              />
            </div>

            {/* Period */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Rotation period
              </label>
              <select
                value={rotationPeriod}
                onChange={(e) => setRotationPeriod(parseInt(e.target.value, 10))}
                className="px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm"
              >
                <option value={7}>1 week</option>
                <option value={14}>2 weeks</option>
              </select>
            </div>

            {/* Members list */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Rotation order
              </label>
              {rotationMembers.length === 0 ? (
                <p className="text-sm text-gray-500 mb-2">No members added yet.</p>
              ) : (
                <div className="space-y-2 mb-3">
                  {rotationMembers.map((member, idx) => (
                    <div
                      key={member.email}
                      className="flex items-center gap-2 bg-gray-50 rounded-md px-3 py-2"
                    >
                      <span className="text-sm font-medium text-gray-500 w-6">{idx + 1}.</span>
                      <span className="flex-1 text-sm text-gray-900">{member.name || member.email}</span>
                      <button
                        onClick={() => moveRotationMember(idx, -1)}
                        disabled={idx === 0}
                        className="text-gray-400 hover:text-gray-600 disabled:opacity-30 px-1"
                        title="Move up"
                      >
                        &uarr;
                      </button>
                      <button
                        onClick={() => moveRotationMember(idx, 1)}
                        disabled={idx === rotationMembers.length - 1}
                        className="text-gray-400 hover:text-gray-600 disabled:opacity-30 px-1"
                        title="Move down"
                      >
                        &darr;
                      </button>
                      <button
                        onClick={() => removeRotationMember(member.email)}
                        className="text-red-500 hover:text-red-700 px-1"
                        title="Remove"
                      >
                        &times;
                      </button>
                    </div>
                  ))}
                </div>
              )}

              {/* Add member dropdown */}
              <select
                value=""
                onChange={(e) => {
                  const email = e.target.value;
                  if (!email) return;
                  const u = users?.find((u) => u.email === email);
                  if (u) addRotationMember(email, u.name || email);
                }}
                className="px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm"
              >
                <option value="">Add member...</option>
                {users
                  ?.filter((u) => !rotationMembers.some((m) => m.email === u.email))
                  .map((u) => (
                    <option key={u.email} value={u.email}>
                      {u.name || u.email}
                    </option>
                  ))}
              </select>
            </div>

            {rotationError && (
              <div className="bg-red-50 border border-red-200 rounded-md p-3">
                <p className="text-sm text-red-600">{rotationError}</p>
              </div>
            )}

            <div className="flex gap-3 pt-2">
              <button
                onClick={handleSaveRotation}
                disabled={updateRotation.isPending}
                className="px-4 py-2 bg-blue-600 text-white text-sm font-medium rounded-md hover:bg-blue-700 disabled:opacity-50"
              >
                {updateRotation.isPending ? 'Saving...' : 'Save'}
              </button>
              <button
                onClick={() => setEditingRotation(false)}
                className="px-4 py-2 bg-white text-gray-700 text-sm font-medium rounded-md border border-gray-300 hover:bg-gray-50"
              >
                Cancel
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

function RotationDisplay({ config }: { config: RotationConfig | null }) {
  if (!config) {
    return <p className="text-sm text-gray-500">No rotation configured. Click "Configure" to set up automatic rotation.</p>;
  }

  return (
    <div className="space-y-3">
      <div className="flex items-center gap-2">
        <span
          className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
            config.enabled ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-500'
          }`}
        >
          {config.enabled ? 'Enabled' : 'Disabled'}
        </span>
        <span className="text-sm text-gray-500">
          {config.periodDays === 7 ? 'Weekly' : `Every ${config.periodDays} days`} starting{' '}
          {new Date(config.startDate + 'T12:00:00Z').toLocaleDateString('en-US', {
            month: 'short',
            day: 'numeric',
            year: 'numeric',
          })}
        </span>
      </div>

      {config.members.length > 0 && (
        <div>
          <p className="text-sm font-medium text-gray-700 mb-1">Rotation order:</p>
          <ol className="list-decimal list-inside text-sm text-gray-600 space-y-0.5">
            {config.members.map((m) => (
              <li key={m.email}>{m.name || m.email}</li>
            ))}
          </ol>
        </div>
      )}

      <p className="text-xs text-gray-400">
        Last updated by {config.updatedBy} on{' '}
        {new Date(config.updatedAt).toLocaleDateString('en-US', {
          month: 'short',
          day: 'numeric',
          year: 'numeric',
        })}
      </p>
    </div>
  );
}
